import React, { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface ActionBarProps {
  children: ReactNode;
  className?: string;
  /** Controls alignment on desktop - default is 'start' */
  align?: "start" | "end" | "center" | "between";
}

/**
 * ActionBar - Responsive wrapper for multiple action buttons/controls
 * 
 * Provides:
 * - flex-wrap behavior to prevent horizontal overflow on mobile
 * - Consistent gap spacing between items
 * - Full-width stacking on mobile, inline on larger screens
 * 
 * @example
 * <ActionBar>
 *   <VersionDropdown />
 *   <StatusManager />
 *   <RefreshButton />
 * </ActionBar>
 */
const ActionBar = ({ children, className, align = "start" }: ActionBarProps) => {
  const alignmentClasses = {
    start: "justify-start",
    end: "justify-end",
    center: "justify-center",
    between: "justify-between",
  };

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2",
        alignmentClasses[align],
        // On mobile, children take full width for better touch targets
        "[&>*]:w-full [&>*]:sm:w-auto",
        className
      )}
    >
      {children}
    </div>
  );
};

export default ActionBar;
